import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  IconButton,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { ClientFormData } from '../types/forms';
import { getClientChanges } from '../utils/changeDetection';

const validatePhoneNumber = (phone: string): boolean => {
  if (!phone || phone.trim() === '') {
    return true; // Empty is valid (optional field)
  }
  
  // Remove all spaces for validation
  const cleanPhone = phone.replace(/\s/g, '');
  
  // Format 1: 9 digits (123456789)
  if (/^\d{9}$/.test(cleanPhone)) {
    return true;
  }
  
  // Format 2: International format with + and digits (+48123456789 or +48 123 456 789)
  // After +, allow 1-3 country code digits, then 7-12 digits
  if (/^\+\d{1,3}\d{7,12}$/.test(cleanPhone)) {
    return true;
  }
  
  return false;
};

interface ClientFormModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data?: ClientFormData | Partial<ClientFormData>) => void;
  formData: ClientFormData;
  onInputChange: (field: keyof ClientFormData) => (event: React.ChangeEvent<HTMLInputElement>) => void;
  title: string;
  submitButtonText: string;
  isEdit?: boolean;
}

const ClientFormModal: React.FC<ClientFormModalProps> = ({
  open,
  onClose,
  onSubmit,
  formData,
  onInputChange,
  title,
  submitButtonText,
  isEdit = false,
}) => {
  const { t } = useTranslation();
  const [originalFormData, setOriginalFormData] = useState<ClientFormData | null>(null);
  const [phoneError, setPhoneError] = useState(false);
  const [touchedPhone, setTouchedPhone] = useState(false);

  // Store original form data when editing
  useEffect(() => {
    if (open && isEdit && !originalFormData) {
      setOriginalFormData({ ...formData });
    } else if (!open) {
      setOriginalFormData(null);
      setPhoneError(false);
      setTouchedPhone(false);
    }
  }, [open, isEdit, formData, originalFormData]);

  // Validate phone number when it changes
  useEffect(() => {
    if (touchedPhone && formData.phone_number) {
      setPhoneError(!validatePhoneNumber(formData.phone_number));
    } else {
      setPhoneError(false);
    }
  }, [formData.phone_number, touchedPhone]);

  const handlePhoneBlur = () => {
    setTouchedPhone(true);
    if (formData.phone_number) {
      setPhoneError(!validatePhoneNumber(formData.phone_number));
    }
  };

  const isFormValid = useMemo(() => {
    const nameValid = formData.name.trim() !== '';
    const phoneValid = !formData.phone_number || validatePhoneNumber(formData.phone_number);
    return nameValid && phoneValid;
  }, [formData.name, formData.phone_number]);

  // Check if form data has changed from original
  const hasFormDataChanged = useMemo(() => {
    if (!isEdit || !originalFormData) {
      return true; // For new clients, always allow submission
    }

    return (
      formData.name !== originalFormData.name ||
      formData.phone_number !== originalFormData.phone_number ||
      formData.notes !== originalFormData.notes
    );
  }, [isEdit, originalFormData, formData]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {title}
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ ml: 1 }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <TextField
            autoFocus
            margin="dense"
            label={t('common.name')}
            fullWidth
            variant="outlined"
            value={formData.name}
            onChange={onInputChange('name')}
            required
          />
          <TextField
            margin="dense"
            label={t('common.phone')}
            fullWidth
            variant="outlined"
            value={formData.phone_number || ''}
            onChange={onInputChange('phone_number')}
            onBlur={handlePhoneBlur}
            error={phoneError}
            helperText={phoneError ? t('clients.invalidPhoneFormat') || 'Invalid phone format. Use: 123456789, 123 456 789, +48123456789, or +48 123 456 789' : ''}
            placeholder="123 456 789 or +48 123 456 789"
          />
          <TextField
            margin="dense"
            label={t('common.notes')}
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={formData.notes || ""}
            onChange={onInputChange('notes')}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={() => {
            if (isEdit && originalFormData) {
              // For edit mode, only send changed fields
              const changedData = getClientChanges(originalFormData, formData);
              onSubmit(changedData);
            } else {
              // For create mode, send all data
              onSubmit(formData);
            }
          }}
          variant="contained"
          disabled={!isFormValid || (isEdit && !hasFormDataChanged)}
        >
          {submitButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClientFormModal;
